import React from "react";
import { formatAkuntansi as fA, formatPengurang as fP } from "@/lib/format";

const tid = (s) => String(s).toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

export function Line({ label, value, indent, strong, total, id, fmt = fA }) {
  const cls = total ? "border-t-2 border-slate-900 font-bold text-base" : strong ? "border-t border-slate-200 font-semibold" : "";
  return (
    <div className={`flex items-baseline justify-between gap-4 py-1.5 ${cls}`} data-testid={id}>
      <span className={indent ? "pl-5 text-slate-600" : ""}>{label}</span>
      <span className="font-mono-num whitespace-nowrap text-right">{fmt(value)}</span>
    </div>
  );
}

export const Group = ({ title }) => <div className="pt-4 pb-1 text-[11px] font-semibold uppercase tracking-wider text-slate-500">{title}</div>;

const Rows = ({ rows, prefix, fmt }) => (rows.length === 0
  ? <div className="pl-5 py-1.5 text-slate-400 text-xs">Tidak ada mutasi.</div>
  : rows.map((r) => <Line key={r.code} indent label={r.name} value={r.amount} fmt={fmt} id={`${prefix}-${tid(r.code)}`} />));

export function PLStatement({ pl }) {
  const b = pl.breakdown; const c = b.cogs;
  return (
    <div className="text-sm" data-testid="pl-statement">
      <Group title="A. Penjualan" />
      <Rows rows={b.sales.rows} prefix="pl-sales" />
      <Line strong label="Jumlah Penjualan" value={b.sales.total} id="pl-sales-total" />
      <Group title="B. Harga Pokok Penjualan" />
      <Line indent label="Persediaan Awal" value={c.opening_inventory} id="pl-cogs-opening" />
      <Line indent label="Pembelian / Produksi (Masuk)" value={c.purchases} id="pl-cogs-purchases" />
      <Line indent label="Barang Tersedia untuk Dijual" value={c.available} id="pl-cogs-available" />
      <Line indent label="Persediaan Akhir" value={c.closing_inventory} fmt={fP} id="pl-cogs-closing" />
      <Line indent label="Penyesuaian / Keluar Non-HPP" value={c.adjustment} id="pl-cogs-adjustment" />
      <Line strong label="Harga Pokok Penjualan" value={c.total} id="pl-cogs-total" />
      <Line strong label="C. Laba/(Rugi) Kotor Usaha" value={pl.gross_profit} id="pl-gross" />
      <Group title="D. Biaya Administrasi & Umum" />
      <Rows rows={b.opex.rows} prefix="pl-opex" />
      <Line strong label="Total Biaya Operasional" value={b.opex.total} id="pl-opex-total" />
      <Line strong label="E. Laba/(Rugi) Usaha" value={pl.operating_income} id="pl-operating" />
      <Group title="F. Pendapatan & Beban Lain" />
      <Rows rows={b.other.rows} prefix="pl-other" />
      <Line strong label="Total Pendapatan & Beban Lain" value={b.other.total} id="pl-other-total" />
      <div className="mt-3 rounded-md bg-slate-900 text-white px-3">
        <Line label={<span className="font-display font-bold text-base">G. Laba/(Rugi) Bersih</span>} value={pl.net_income} id="report-pl-net" />
      </div>
    </div>
  );
}

export function BSStatement({ bs }) {
  const b = bs.breakdown; const e = b.equity;
  return (
    <div className="text-sm" data-testid="bs-statement">
      <Group title="A. Aset Lancar" />
      <Rows rows={b.current_assets.rows} prefix="bs-ca" />
      <Line strong label="Jumlah Aset Lancar" value={b.current_assets.total} id="bs-ca-total" />
      <Group title="B. Aset Tetap" />
      <Rows rows={b.fixed_assets.rows} prefix="bs-fa" />
      <Line strong label="Jumlah Aset Tetap (Bersih)" value={b.fixed_assets.total} id="bs-fa-total" />
      {b.other_assets.rows.length > 0 && <>
        <Group title="Aset Lainnya" />
        <Rows rows={b.other_assets.rows} prefix="bs-oa" />
        <Line strong label="Jumlah Aset Lainnya" value={b.other_assets.total} id="bs-oa-total" />
      </>}
      <Line total label="C. TOTAL ASET" value={bs.assets} id="report-bs-assets" />
      <Group title="D. Kewajiban / Liabilitas" />
      <Rows rows={b.liabilities.rows} prefix="bs-liab" />
      <Line strong label="Jumlah Kewajiban" value={bs.liabilities} id="bs-liab-total" />
      <Group title="E. Modal / Ekuitas" />
      {e.rows.map((r) => <Line key={r.code} indent label={r.name} value={r.amount} id={`bs-eq-${tid(r.code)}`} />)}
      <Line indent label="Prive" value={e.prive} fmt={fP} id="bs-eq-prive" />
      <Line indent label="Laba Ditahan s.d. Awal Periode" value={e.retained_before} id="bs-eq-retained" />
      <Line indent label="Laba/(Rugi) Periode Berjalan" value={e.current_period} id="bs-eq-current" />
      <Line strong label="Jumlah Ekuitas" value={bs.total_equity} id="bs-eq-total" />
      <Line total label="F. TOTAL LIABILITAS + EKUITAS" value={bs.total_liabilities_equity} id="bs-le-total" />
      <div className={`mt-3 p-2 rounded-md text-xs ${bs.balanced ? "bg-emerald-50 text-emerald-800" : "bg-rose-50 text-rose-800 font-medium"}`} data-testid="bs-balance-check">
        {bs.balanced ? "✓ Neraca seimbang — Total Aset = Total Liabilitas + Ekuitas." : `⚠ Neraca TIDAK seimbang. Selisih ${fA(bs.difference)}. Periksa saldo awal & jurnal ekuitas. Data tidak diubah otomatis.`}
      </div>
    </div>
  );
}
